/**
 * Inventory Display Component
 * Shows team inventory in a grid format
 */

import React, { useState, useEffect, useRef } from 'react';
import { InventoryItem } from '../models/Inventory';
import { IoCheckmark } from 'react-icons/io5';
import { InventoryIcon } from '../utils/inventoryIcons';

interface InventoryDisplayProps {
  inventory: InventoryItem[];
  highlightItems?: string[];
  onItemClick?: (item: InventoryItem) => void;
  selectable?: boolean;
  selectedItem?: string;
  compact?: boolean;
  fillHeight?: boolean;
  className?: string;
}

export const InventoryDisplay: React.FC<InventoryDisplayProps> = ({
  inventory,
  highlightItems = [],
  onItemClick,
  selectable = false,
  selectedItem,
  compact = false,
  fillHeight = false,
  className = ''
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  // Check if content is scrollable and update indicator visibility
  useEffect(() => {
    const checkScrollable = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const isScrollable = container.scrollHeight > container.clientHeight;
      const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 5;

      setShowScrollIndicator(isScrollable && !isAtBottom);
    };

    checkScrollable();
    const container = scrollContainerRef.current;

    if (container) {
      container.addEventListener('scroll', checkScrollable);
      window.addEventListener('resize', checkScrollable);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScrollable);
      }
      window.removeEventListener('resize', checkScrollable);
    };
  }, [inventory]);

  const handleItemClick = (item: InventoryItem) => {
    if (selectable && onItemClick) {
      onItemClick(item);
    }
  };

  const isHighlighted = (itemId: string): boolean => {
    return highlightItems.includes(itemId);
  };

  const isSelected = (itemId: string): boolean => {
    return selectedItem === itemId;
  };

  if (inventory.length === 0) {
    return (
      <div className={className}>
        <p className="text-center text-gray-500 py-4">No items in inventory</p>
      </div>
    );
  }

  if (compact) {
    // When fillHeight is true, parent handles scrolling
    const containerClasses = fillHeight
      ? "space-y-2"
      : "space-y-2 max-h-64 overflow-y-auto scrollbar-thin";

    return (
      <div className={`${className} ${fillHeight ? '' : 'relative'}`}>
        <div ref={fillHeight ? null : scrollContainerRef} className={containerClasses}>
          {inventory.map((item) => {
            let itemClasses = 'flex items-center justify-between gap-2 py-1.5 border-b border-gray-100 last:border-0';
            if (selectable) itemClasses += ' cursor-pointer hover:bg-gray-50';
            if (isSelected(item.id)) itemClasses += ' bg-green-100 border-green-500';
            if (isHighlighted(item.id)) itemClasses += ' bg-yellow-50';

            return (
              <div
                key={item.id}
                className={itemClasses}
                onClick={() => handleItemClick(item)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <InventoryIcon itemId={item.id} className="w-5 h-5 flex-shrink-0" />
                  <span className={`text-sm ${isSelected(item.id) ? 'text-green-800 font-semibold' : 'text-black'}`}>{item.name}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isSelected(item.id) && <span className="text-green-600"><IoCheckmark /></span>}
                  <span className="text-sm font-semibold text-gray-700">×{item.quantity}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Scroll Indicator - only when not fillHeight */}
        {!fillHeight && showScrollIndicator && (
          <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none bg-gradient-to-t from-white via-white/80 to-transparent flex items-end justify-center pb-2">
            <div className="flex items-center gap-1 text-xs text-gray-500 font-medium">
              <span>Scroll for more</span>
              <svg className="w-3 h-3 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        )}
      </div>
    );
  }

  // When fillHeight is true, parent handles scrolling
  const gridContainerClasses = fillHeight
    ? "grid grid-cols-2 md:grid-cols-3 gap-3"
    : "grid grid-cols-2 md:grid-cols-3 gap-3 max-h-96 overflow-y-auto scrollbar-thin";

  return (
    <div className={`${className} ${fillHeight ? '' : 'relative'}`}>
      <div ref={fillHeight ? null : scrollContainerRef} className={gridContainerClasses}>
        {inventory.map((item) => {
          let itemClasses = 'bg-white border border-gray-200 rounded-lg p-3 transition-all';
          if (selectable) itemClasses += ' cursor-pointer hover:border-gray-400 hover:shadow-sm';
          if (isSelected(item.id)) itemClasses += ' border-green-500 bg-green-100 shadow-sm ring-2 ring-green-200';
          if (isHighlighted(item.id)) itemClasses += ' border-yellow-500 bg-yellow-50';

          return (
            <div
              key={item.id}
              className={itemClasses}
              onClick={() => handleItemClick(item)}
            >
              <div className="flex items-center gap-3">
                <InventoryIcon itemId={item.id} className="w-10 h-10" />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium truncate ${isSelected(item.id) ? 'text-green-800' : 'text-black'}`}>
                    {item.name}
                  </div>
                  <div className="text-xs text-gray-600">Qty: {item.quantity}</div>
                </div>
                {isSelected(item.id) && (
                  <span className="text-green-600 text-xl"><IoCheckmark /></span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Scroll Indicator - only when not fillHeight */}
      {!fillHeight && showScrollIndicator && (
        <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none bg-gradient-to-t from-white via-white/80 to-transparent flex items-end justify-center pb-2">
          <div className="flex items-center gap-1 text-xs text-gray-500 font-medium">
            <span>Scroll for more</span>
            <svg className="w-3 h-3 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryDisplay;
